import { Ticker, type Container } from 'pixi.js'

/** Returns true when the user prefers reduced motion. */
export function prefersReducedMotion(): boolean {
  return globalThis.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false
}

export type EasingFn = (t: number) => number

export const Easing = {
  easeOutCubic: (t: number) => 1 - Math.pow(1 - t, 3),
  easeInCubic: (t: number) => t * t * t,
  easeOutBack: (t: number) => {
    const c1 = 1.70158
    const c3 = c1 + 1
    return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2)
  },
  easeInOutSine: (t: number) => -(Math.cos(Math.PI * t) - 1) / 2,
  linear: (t: number) => t,
}

interface TweenOptions {
  target: Record<string, any>
  props: Record<string, number>
  duration: number
  easing?: EasingFn
  delay?: number
  onComplete?: () => void
  ticker: Ticker
}

/** A simple property tween driven by a PixiJS Ticker. Returns a cancel function. */
export function tween(opts: TweenOptions): () => void {
  const { target, props, duration, easing = Easing.easeOutCubic, delay = 0, onComplete, ticker } = opts

  // Reduced motion: apply final values immediately
  if (prefersReducedMotion()) {
    for (const key of Object.keys(props)) {
      target[key] = props[key]
    }
    onComplete?.()
    return () => {}
  }

  const startValues: Record<string, number> = {}
  for (const key of Object.keys(props)) {
    startValues[key] = target[key] as number
  }

  let elapsed = -delay
  let cancelled = false

  const update = (dt: Ticker) => {
    if (cancelled) return

    elapsed += dt.deltaMS

    if (elapsed < 0) return // still in delay

    const progress = Math.min(elapsed / duration, 1)
    const eased = easing(progress)

    for (const key of Object.keys(props)) {
      target[key] = startValues[key] + (props[key] - startValues[key]) * eased
    }

    if (progress >= 1) {
      ticker.remove(update)
      onComplete?.()
    }
  }

  ticker.add(update)

  return () => {
    cancelled = true
    ticker.remove(update)
  }
}

/** Pulse an object's alpha between min and max. Returns a cancel function. */
export function pulseAlpha(
  target: Container,
  ticker: Ticker,
  min: number = 0.3,
  max: number = 0.7,
  periodMs: number = 2000,
): () => void {
  // Reduced motion: set a static alpha midpoint
  if (prefersReducedMotion()) {
    target.alpha = (min + max) / 2
    return () => {}
  }

  let elapsed = 0
  let cancelled = false

  const update = (dt: Ticker) => {
    if (cancelled) return
    elapsed += dt.deltaMS
    const t = (Math.sin((elapsed / periodMs) * Math.PI * 2) + 1) / 2
    target.alpha = min + (max - min) * t
  }

  ticker.add(update)
  return () => {
    cancelled = true
    ticker.remove(update)
  }
}
